/**
 * CRM Settings — Lookup Tables
 *
 * A single page to edit any of the 9 CRM reference tables
 * (lead_sources, industries, account_types, etc). Supports inline
 * edit for name/color/sort_order, soft delete, create, and
 * up/down reorder (calls reorder with the permuted id list).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { ArrowDown, ArrowUp, Loader2, Plus, Sparkles, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/crm/lookups')({
  component: LookupsPage,
})

const TABLES = [
  { value: 'lead_sources', label: 'Lead Sources' },
  { value: 'industries', label: 'Industries' },
  { value: 'account_types', label: 'Account Types' },
  { value: 'lifecycle_stages', label: 'Lifecycle Stages' },
  { value: 'deal_types', label: 'Deal Types' },
  { value: 'forecast_categories', label: 'Forecast Categories' },
  { value: 'close_reasons', label: 'Close Reasons' },
  { value: 'competitors', label: 'Competitors' },
  { value: 'campaigns', label: 'Campaigns' },
] as const

type TableName = (typeof TABLES)[number]['value']

interface LookupRow {
  id: string
  key: string
  name: string
  description: string | null
  color: string | null
  sortOrder: number
  active: boolean
}

function LookupsPage() {
  const t = useTranslations()
  const qc = useQueryClient()
  const [table, setTable] = useState<TableName>('lead_sources')

  const listQ = useQuery(
    orpc.crm.lookups.list.queryOptions({ input: { table, includeInactive: true } }),
  )
  const rows = (listQ.data ?? []) as LookupRow[]

  const invalidate = () =>
    qc.invalidateQueries({
      queryKey: orpc.crm.lookups.list.queryKey({ input: { table, includeInactive: true } }),
    })

  const updateMut = useMutation(
    orpc.crm.lookups.update.mutationOptions({
      onSuccess: () => invalidate(),
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const deleteMut = useMutation(
    orpc.crm.lookups.delete.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.common.delete'))
        invalidate()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const reorderMut = useMutation(
    orpc.crm.lookups.reorder.mutationOptions({
      onSuccess: () => invalidate(),
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const createMut = useMutation(
    orpc.crm.lookups.create.mutationOptions({
      onSuccess: () => {
        toast.success(t('crm.settings.lookups.newEntry'))
        setCreateOpen(false)
        setForm({ key: '', name: '', color: '' })
        invalidate()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState({ key: '', name: '', color: '' })
  const [seedDialogOpen, setSeedDialogOpen] = useState(false)
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    id: string | null
    name: string
  }>({
    open: false,
    id: null,
    name: '',
  })

  const seedMut = useMutation(
    orpc.crm.admin.seedDefaults.mutationOptions({
      onSuccess: (result) => {
        const r = result as {
          lookupsAdded: number
          pipelineCreated: boolean
          stagesAdded: number
          currencyCreated: boolean
        }
        const parts: string[] = []
        if (r.lookupsAdded > 0) parts.push(`${r.lookupsAdded} lookups`)
        if (r.pipelineCreated) parts.push(`pipeline + ${r.stagesAdded} stages`)
        if (r.currencyCreated) parts.push('currency settings')
        toast.success(
          parts.length > 0
            ? `Seeded ${parts.join(', ')}`
            : t('crm.settings.seed.alreadySeeded') || 'Already seeded — nothing to do',
        )
        setSeedDialogOpen(false)
        // Refresh every query family the seed can affect. `.key()` is
        // the argless prefix match, so every cached variant (different
        // `table` filters, different `pipelineId`, etc.) invalidates
        // in one sweep.
        qc.invalidateQueries({ queryKey: orpc.crm.lookups.list.key() })
        qc.invalidateQueries({ queryKey: orpc.crm.pipelines.list.key() })
        qc.invalidateQueries({ queryKey: orpc.crm.stages.list.key() })
        qc.invalidateQueries({ queryKey: orpc.crm.currency.settings.get.key() })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir
    if (target < 0 || target >= rows.length) return
    const ids = rows.map((r) => r.id)
    ;[ids[index], ids[target]] = [ids[target]!, ids[index]!]
    reorderMut.mutate({ table, ids })
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('crm.settings.lookups.title')}</h1>
          <p className="text-muted-foreground">{t('navigation.crmSettings')}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('crm.settings.lookups.newEntry')}
        </Button>
      </div>

      <Card className="mb-6 border-dashed">
        <CardHeader className="flex flex-row items-start gap-4 space-y-0">
          <Sparkles className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
          <div className="flex-1">
            <CardTitle className="text-base">
              {t('crm.settings.seed.title') || 'Seed CRM defaults'}
            </CardTitle>
            <CardDescription>
              {t('crm.settings.seed.description') ||
                'Populate the 9 lookup tables, default pipeline with 6 stages, and currency settings. Idempotent — safe to run on an already-seeded workspace.'}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            onClick={() => setSeedDialogOpen(true)}
            disabled={seedMut.isPending}
          >
            {seedMut.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              t('crm.settings.seed.button') || 'Seed defaults'
            )}
          </Button>
        </CardHeader>
      </Card>

      <div className="mb-4 max-w-xs">
        <Label htmlFor="table-select">{t('crm.settings.lookups.chooseTable')}</Label>
        <Select value={table} onValueChange={(v) => setTable(v as TableName)}>
          <SelectTrigger id="table-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TABLES.map((tbl) => (
              <SelectItem key={tbl.value} value={tbl.value}>
                {tbl.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <div className="p-6 space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              {t('crm.common.noRecords')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">{t('crm.settings.lookups.sortOrder')}</TableHead>
                  <TableHead>{t('crm.settings.lookups.name')}</TableHead>
                  <TableHead>{t('crm.settings.lookups.key')}</TableHead>
                  <TableHead className="w-32">{t('crm.settings.lookups.color')}</TableHead>
                  <TableHead className="w-28">{t('crm.settings.lookups.sortOrder')}</TableHead>
                  <TableHead className="w-20">{t('crm.settings.lookups.active')}</TableHead>
                  <TableHead className="w-20 text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={r.id} className={r.active ? '' : 'opacity-50'}>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={i === 0}
                          onClick={() => move(i, -1)}
                        >
                          <ArrowUp className="h-3 w-3" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={i === rows.length - 1}
                          onClick={() => move(i, 1)}
                        >
                          <ArrowDown className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Input
                        defaultValue={r.name}
                        onBlur={(e) => {
                          if (e.target.value !== r.name) {
                            updateMut.mutate({ table, id: r.id, name: e.target.value })
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {r.key}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="color"
                        defaultValue={r.color ?? '#888888'}
                        onBlur={(e) => {
                          if (e.target.value !== r.color) {
                            updateMut.mutate({ table, id: r.id, color: e.target.value })
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        defaultValue={r.sortOrder}
                        onBlur={(e) => {
                          const v = Number(e.target.value)
                          if (v !== r.sortOrder) {
                            updateMut.mutate({ table, id: r.id, sortOrder: v })
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>{r.active ? t('crm.settings.lookups.active') : '-'}</TableCell>
                    <TableCell className="text-right">
                      {r.active && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                          onClick={() => setDeleteDialog({ open: true, id: r.id, name: r.name })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('crm.settings.lookups.newEntry')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="l-key">{t('crm.settings.lookups.key')}</Label>
              <Input
                id="l-key"
                value={form.key}
                onChange={(e) => setForm({ ...form, key: e.target.value })}
                placeholder="lowercase_underscore"
              />
              <p className="text-xs text-muted-foreground mt-1">
                lowercase, underscores only, starts with a letter
              </p>
            </div>
            <div>
              <Label htmlFor="l-name">{t('crm.settings.lookups.name')}</Label>
              <Input
                id="l-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="l-color">{t('crm.settings.lookups.color')}</Label>
              <Input
                id="l-color"
                type="color"
                value={form.color || '#888888'}
                onChange={(e) => setForm({ ...form, color: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t('crm.common.cancel')}
            </Button>
            <Button
              onClick={() =>
                createMut.mutate({
                  table,
                  key: form.key,
                  name: form.name,
                  color: form.color || undefined,
                })
              }
              disabled={!form.key || !form.name || createMut.isPending}
            >
              {createMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('crm.common.create')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) =>
          setDeleteDialog({
            open,
            id: open ? deleteDialog.id : null,
            name: open ? deleteDialog.name : '',
          })
        }
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('crm.common.delete')} "{deleteDialog.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>{t('crm.common.required')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('crm.common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground"
              onClick={() => {
                if (deleteDialog.id) deleteMut.mutate({ table, id: deleteDialog.id })
                setDeleteDialog({ open: false, id: null, name: '' })
              }}
            >
              {t('crm.common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={seedDialogOpen} onOpenChange={setSeedDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('crm.settings.seed.confirmTitle') || 'Seed CRM defaults?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('crm.settings.seed.confirmBody') ||
                'This inserts default rows into the 9 CRM lookup tables, creates a default sales pipeline with 6 stages (if none exists), and ensures the currency settings row exists. The operation is idempotent — existing rows are preserved.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('crm.common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => seedMut.mutate({})}>
              {t('crm.settings.seed.button') || 'Seed defaults'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
